
import React from 'react';

/**
 * Application memory management utilities
 */

class MemoryManager {
  private cleanupTasks: Array<() => void> = [];
  private memoryThreshold = 100 * 1024 * 1024; // 100MB
  private checkInterval: NodeJS.Timeout | null = null;

  constructor() {
    this.startMonitoring();
  }

  private startMonitoring() {
    // Check memory usage every 30 seconds
    this.checkInterval = setInterval(() => {
      this.checkMemoryUsage();
    }, 30000);
  }

  private checkMemoryUsage() {
    if ('memory' in performance) {
      const memInfo = (performance as any).memory;
      if (memInfo.usedJSHeapSize > this.memoryThreshold) {
        console.warn('High memory usage detected, running cleanup');
        this.runCleanup();
      }
    }
  }

  addCleanupTask(task: () => void) {
    this.cleanupTasks.push(task);
  }

  removeCleanupTask(task: () => void) {
    const index = this.cleanupTasks.indexOf(task);
    if (index > -1) {
      this.cleanupTasks.splice(index, 1);
    }
  }

  runCleanup() {
    this.cleanupTasks.forEach(task => {
      try {
        task();
      } catch (error) {
        console.error('Error running cleanup task:', error);
      }
    });
  }

  destroy() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    this.cleanupTasks = [];
  }
}

export const memoryManager = new MemoryManager();

// React hook for component memory management
export const useMemoryCleanup = (cleanupFn: () => void) => {
  React.useEffect(() => {
    memoryManager.addCleanupTask(cleanupFn);
    
    return () => {
      memoryManager.removeCleanupTask(cleanupFn);
    };
  }, [cleanupFn]);
};

// Debounced function utility to reduce memory pressure from rapid calls
export const createMemoryOptimizedDebounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): T => {
  let timeoutId: NodeJS.Timeout;
  let lastArgs: Parameters<T>;
  
  const cleanup = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };
  
  memoryManager.addCleanupTask(cleanup);
  
  return ((...args: Parameters<T>) => {
    lastArgs = args;
    clearTimeout(timeoutId);
    
    timeoutId = setTimeout(() => {
      func.apply(null, lastArgs);
    }, delay);
  }) as T;
};
